import styled, { css, keyframes } from 'styled-components';
import {
  Divider,
  Flex,
  Icon,
  isNotDarkThemeMixin,
  Link,
  styleMixinFocusOutline,
  TextButton,
} from '@amzn/storm-ui';
import { orange } from 'src/colors';

const easeCurve = `cubic-bezier(0.2, 0, 0.38, 0.9)`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 100%;
  }
`;

const reducedMotionMediaQuery = css`
  @media screen and (prefers-reduced-motion: reduce) {
    & {
      transition: none !important;
    }
  }
`;

export const HiddenContent = styled(Flex)`
  opacity: 0;
  width: 100%;
`;

export const AllToolsHiddenContent = styled.div<{ width: string }>`
  display: flex; // TODO: use Flex component from StormUI
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: ${({ theme }) => theme.spacing.xxxsmall.default};
  padding-inline-start: ${({ theme }) => theme.spacing.xxsmall.default};
  max-height: 100%;
  overflow: auto;
  min-width: ${({ width }) =>
    width} !important; // TODO: increase selector's specificity instead of using !important
  pointer-events: none;
  opacity: 0;
  width: 0;
  overflow: hidden;
`;

export const ToolbarWrapper = styled.div<{ isNavOpen: boolean }>`
  background-color: ${({ theme }) => theme.surface.container.default};
  border-inline-end: 1px solid
    ${({ theme }) => theme.elevation.borderColor.subtle};
  height: 100%;
  width: 64px;
  z-index: 300;
  display: flex;
  flex-direction: column;

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      width: 320px;

      & ${HiddenContent} {
        opacity: 1;
        animation: ${fadeIn} 0.15s linear;
      }
    `}
`;

export const NavItemsContainer = styled(Flex).attrs({
  direction: 'column',
})`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
`;

export const ToolbarOutterWrapper = styled.div<{
  isNavOpen: boolean;
  isAllToolsOpen: boolean;
}>`
  display: flex; // TODO: use Flex component instead
  background-color: ${({ theme }) => theme.surface.container.default};
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;
  width: 64px;
  z-index: 300;
  max-width: 100%;

  &:hover {
    overflow: hidden auto;
  }

  ${isNotDarkThemeMixin(css`
    box-shadow: ${({ theme }) => theme.elevation.shadow.panel};
  `)};

  ${reducedMotionMediaQuery};
  -webkit-transition: all 100ms linear;
  -moz-transition: all 100ms linear;
  -o-transition: all 100ms linear;
  -ms-transition: all 100ms linear;
  transition: all 100ms linear;

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      width: 320px;

      & ${HiddenContent} {
        opacity: 1;
        animation: ${fadeIn} 0.15s linear;
      }
    `}

  ${({ isAllToolsOpen }) =>
    isAllToolsOpen &&
    css`
      width: 100%;

      & ${AllToolsHiddenContent} {
        opacity: 1;
        pointer-events: auto;
        width: 100%;
        animation: ${fadeIn} 0.15s linear;
      }
    `}
`;

export const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 64px;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.nav.overlay.backgroundColor};
  opacity: 10%;
  z-index: 51;
`;

const SidenavIndicator = css`
  content: '';
  height: 24px;
  width: 3px;
  position: absolute;
  top: 8px;
  left: -12px;
  border-radius: 0
    ${({ theme }) =>
      `${theme.border.radius.small} ${theme.border.radius.small}`}
    0;
`;

const SelectedSectionIndicator = css`
  ::before {
    ${SidenavIndicator};
    background-color: ${orange};
  }
`;

const HoverSectionIndicator = css`
  ::before {
    ${SidenavIndicator};
    background-color: ${({ theme }) => theme.color.gray[600]};
  }
`;

export const SideNavLinkOuterWrapper = styled.div<{ isSelected: boolean }>`
  min-height: 40px;
  width: 296px;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
  position: relative;
  box-sizing: border-box;

  ${({ isSelected }) =>
    isSelected
      ? SelectedSectionIndicator
      : css`
          :hover {
            ${HoverSectionIndicator};
          }
        `};
`;

const sideNavLinkStyles = css<{ isSelected: boolean; isNavOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.border.radius.large};
  cursor: pointer;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 40px;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};
  }

  && [id^='aac-set-landing-nav'],
  && [id^='aac-unset-landing-nav'] {
    margin-inline-end: -${({ theme }) => theme.spacing.xsmall.default};
  }

  && [id^='aac-set-landing-nav'] {
    opacity: 0;
    pointer-events: none;
    position: absolute;
  }

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      :hover,
      :focus-visible {
        background-color: ${({ theme }) =>
          theme.selectableContainer.backgroundColor.hover};

        && [id^='aac-set-landing-nav'] {
          opacity: 1;
          pointer-events: auto;
          position: relative;
        }
      }

      && [id^='aac-set-landing-nav']:focus-visible {
        opacity: 1;
        pointer-events: auto;
        position: relative;
      }
    `}
`;

const focusOutlineMixin = css<{ isNavOpen: boolean }>`
  ${({ isNavOpen }) =>
    isNavOpen
      ? css`
          &:focus-visible {
            ${styleMixinFocusOutline};
          }
        `
      : css`
          &:focus-visible {
            outline: none;
          }
        `};
`;

export const SideNavLinkInnerWrapper = styled(Link)<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};

  &:link,
  &:visited {
    color: ${({ theme }) => theme.text.color.default};
    text-decoration: none;
  }

  ${({ isSelected, isNavOpen, theme }) =>
    isSelected &&
    isNavOpen &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.hover};
      font-weight: 700;
    `};

  ${focusOutlineMixin};
`;

export const SideNavButtonInnerWrapper = styled(TextButton)`
  ${sideNavLinkStyles};
  ${focusOutlineMixin};
  &:hover,
  &:active,
  &:focus-visible {
    text-decoration: none;
    color: ${({ theme }) => theme.color.gray[800]};
  }
`;

export const SideNavLinkTitleWrapper = styled.span<{
  isAllToolsOpen?: boolean;
  isFromAllTools?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${({ isFromAllTools, isAllToolsOpen }) => (isFromAllTools && isAllToolsOpen ? 'bold' : 'normal')};
  transition: font-weight 150ms ease;
  }
`;

export const SvgWrapper = styled(Flex)<{ isSelected: boolean }>`
  box-sizing: unset;
  cursor: pointer;
  margin-inline-end: ${({ theme }) => theme.spacing.small.default};
`;

export const AccordionWrapper = styled.div<{ isActive: boolean }>`
  width: 296px;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const AccordionButtonOuterWrapper = styled.div<{
  isSelected: boolean;
}>`
  position: relative;

  ${({ isSelected }) =>
    isSelected
      ? SelectedSectionIndicator
      : css`
          :hover {
            ${HoverSectionIndicator};
          }
        `};
`;

export const AccordionButtonInnerWrapper = styled.button<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};
  text-align: initial;

  ${({ isSelected }) =>
    isSelected &&
    css`
      font-weight: 700;
    `};

  ${focusOutlineMixin};
`;

export const AccordionDropdownIcon = styled(Icon)`
  margin-inline-start: auto;
`;

export const AccordionContentOuterWrapper = styled.div<{ isActive: boolean }>`
  overflow: hidden;
  padding-inline-start: 19px;
  ${reducedMotionMediaQuery};
  transition: max-height 0.1s ${easeCurve};
  max-height: 0;

  ${({ isActive }) =>
    isActive &&
    css`
      transition: max-height 0.25s ${easeCurve};
      overflow: initial;
    `}
`;

export const AccordionContentInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.xsmall.default};
  padding-inline-start: ${({ theme }) => theme.spacing.base.default};
  border-inline-start: 2px solid
    ${({ theme }) => theme.border.color.subtle.default};
`;

export const AccordionContentGroupWrapper = styled(HiddenContent)`
  box-sizing: border-box;
`;

export const AccordionContentSubTitle = styled.span`
  color: ${({ theme }) => theme.text.color.default};

  &&& {
    font-size: 12px;
  }

  font-weight: bold;
  line-height: 1.333;
  padding: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} 0 ${theme.spacing.xxxsmall.default} ${theme.spacing.xxsmall.default}`};
  margin-bottom: ${({ theme }) => theme.spacing.xxsmall.default};
`;

export const AccordionLinkWrapper = styled.div<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};
  padding-inline-start: 0;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.selected};
    `};
`;

export const AccordionLink = styled(Link)<{
  isSelected: boolean;
}>`
  padding: ${({ theme }) => theme.spacing.xxsmall.default};
  padding-inline-start: ${({ theme }) => theme.spacing.xxsmall.default};
  border-radius: ${({ theme }) => theme.border.radius.large};
  flex-grow: 1;

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};

  &:link,
  &:visited {
    color: ${({ theme }) => theme.text.color.default};
    text-decoration: none;
  }

  ${({ isSelected }) => isSelected && `font-weight: 700;`};

  &&&:focus-visible {
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.default};
  }
`;

export const StyledDivider = styled(Divider)`
  margin: 5px 12px;
`;
